
import { useMsal } from '@azure/msal-react';
import { DocumentIcon, XMarkIcon } from '@heroicons/react/20/solid';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridReact } from 'ag-grid-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { File, UserInfo, fileDelete, fileDownload, fileList, fileSummarize, fileUpload, syncRagaas } from './service';


const UserFiles: React.FC = () => {
	const { instance } = useMsal();
	const accounts = instance.getAllAccounts();

	const [user, setUser] = useState<UserInfo>({
		first_name: accounts[0].name ? accounts[0].name.split(", ")[1] : "",
		last_name: accounts[0].name ? accounts[0].name.split(", ")[0] : "",
		user_email: accounts[0].username,
		user_id: accounts[0].homeAccountId
	})

	const [storageList, setStorageList] = useState<Array<File>>([]);
	const [firstList, setFirstList] = useState(false);
	const [summary, setSummary] = useState<Array<string>>([]);
	const [download, setDownload] = useState('');
	const [fileInfo, setFileInfo] = useState<Array<string>>([]);
	const [errorText, setErrorText] = useState('');

	const summaryDialog = useRef<HTMLDialogElement>(null);
	const downloadDialog = useRef<HTMLDialogElement>(null);
	const errorDialog = useRef<HTMLDialogElement>(null);

	const fetchFileList = async () => {
		await syncRagaas(user);
		const response = await fileList(user);
		const rawFiles = [];

		for (const file of response.folders_files) {
			if (file.raw_file == "true") {
				console.log("Listing: ", file.file_name, "\nRaw flag:", file.raw_file, "\nExt flag:", file.ext_file, "\nDate:", file.file_updated_date, "\nSize:", file.file_size_kb)
				rawFiles.push(file);
			}

		}
		setStorageList(rawFiles);
		setFirstList(true);
	}

	useEffect(() => {
		fetchFileList();
	}, [])

	const fetchSummary = async (target: File) => {
		const summary = await fileSummarize(target, user);
		return summary;
	}
	const fetchDownload = async (target: File) => {
		const download = await fileDownload(target, user);
		return download;
	}

	const gridRef = useRef<AgGridReact>(null);
	const [loading, setLoading] = useState<boolean>(false);

	const rowData = useMemo(
		() =>
			storageList.map((file, index) => ({
				fileName: file.file_name,
				filePath: file.file_path,
				bucketName: file.bucket_name,
				folderPath: file.folder_path,
				rawFile: file.raw_file,
				extFile: file.ext_file,
				size: file.file_size_kb,
				date: file.file_updated_date,
			})),
		[storageList]
	);

	const FileCellRenderer = (params: any) => {
		return (
			<div className="text-[12px] font-medium mt-1 flex items-center justify-between px-2 py-2 leading-4">
				<button><DocumentIcon width={16} height={16} className="flex-2" /></button><p className="flex-1 items-center justify-between px-2 py-2 leading-4">{params.data.fileName}</p>
			</div>
		)
	}

	const ActionCellRenderer = (params: any) => {

		const handleSummarize = async () => {
			console.log('Selecting:', params.data);
			summaryDialog.current?.showModal();
			setSummary(['Loading Summary...']);

			const extFolder = `${params.data.folderPath.split('raw_files')[0]}ext_files`;
			const extName = `${params.data.fileName.split('.')[0]}.txt`;
			const extPath = `${extFolder}/${extName}`;

			try {
				const sum = await fetchSummary({
					bucket_name: params.data.bucketName,
					folder_path: extFolder,
					raw_file: params.data.rawFile,
					ext_file: params.data.extFile,
					file_path: extPath,
					file_name: extName,
					file_size_kb: params.data.size,
					file_updated_date: params.data.date,
				});
				const points = sum.split('*');
				const cleanPoints = [];
				for (const point of points) {
					if (point.length > 1)
						cleanPoints.push(point);
				}
				setSummary(cleanPoints);
			}
			catch (error) {
				console.error("Summarization Error");
				setSummary(["Summarization Error", "Check that your file is the correct size and type", "If you are sure the file is correct, try again later"])
			}


		};

		const handleDownload = async () => {
			downloadDialog.current?.showModal()
			setDownload('Loading Download Link...');
			setFileInfo([params.data.fileName, params.data.size]);

			try {
				setDownload(await fetchDownload({
					bucket_name: params.data.bucketName,
					folder_path: params.data.folderPath,
					raw_file: params.data.rawFile,
					ext_file: params.data.extFile,
					file_path: params.data.filePath,
					file_name: params.data.fileName,
					file_size_kb: params.data.size,
					file_updated_date: params.data.date,
				}));
			}
			catch (error) {
				console.error("Download Error");
				setErrorText('Error generating Download link. Check that file still exists')
				errorDialog.current?.showModal();
			}
		}

		const handleDelete = async (target: File) => {

			console.log('Delete action for:', target);
			try {
				await fileDelete(target, user);
				fetchFileList();
			}
			catch (error) {
				console.log('Error deleting');

			}
		};

		return (
			<div className="text-[12px] font-medium mt-1">
				<div className="flex items-center gap-2 text-black">
					<button
						className="flex items-center justify-between px-2 py-2 leading-4 border rounded"
						title="Summarize the file, and access download link"
						onClick={handleSummarize}
					>
						Summary
					</button>
					<button
						className="flex items-center justify-between px-2 py-2 leading-4 border rounded"
						title="Summarize the file, and access download link"
						onClick={handleDownload}
					>
						Download
					</button>
					<button
						className="flex items-center justify-between px-2 py-2 leading-4 border rounded"
						title="Delete this file"
						onClick={() => handleDelete({
							bucket_name: params.data.bucketName,
							folder_path: params.data.folderPath,
							raw_file: params.data.rawFile,
							ext_file: params.data.extFile,
							file_path: params.data.filePath,
							file_name: params.data.fileName,
							file_size_kb: params.data.size,
							file_updated_date: params.data.date,
						})}
					>
						<XMarkIcon width={16} height={16} color={"RED"} /> Delete
					</button>
				</div>
			</div>
		);
	};
	const colDefs = useMemo(() => {
		return [
			{
				field: "fileName",
				headerName: "File Name",
				width: 150,
				cellRenderer: FileCellRenderer,
			},
			{ field: "date", headerName: "Last Modified", flex: 1 },
			{ field: "size", headerName: "File Size (KB)", flex: 1 },
			{
				field: "Action",
				headerName: "",
				width: 275,
				editable: false,
				sortable: false,
				suppressChangeDetection: true,
				cellRenderer: ActionCellRenderer,
			},
		];
	}, []);

	const defaultColDef = useMemo(
		() => ({
			sortable: true,
			filter: true,
			wrapText: false,
			autoHeight: true,
			cellStyle: { padding: '8px 14px', wordWrap: 'break-word' },
		}),
		[]
	);

	const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
		try {
			if (e.target.files) {
				const file = e.target.files[0];
				console.log("Raw file:" + file);
				console.log(file);
				if (file.size < 31457280) {
					const formData = new FormData();
					formData.append("file", file);
					for (var data of formData.entries()) {
						console.log("Form Data:" + data);
						console.log(data);
					}
					await fileUpload(formData, user);
					fetchFileList();
				}
				else {
					setErrorText('File is too large. Uploads must be less than 30 MB')
					errorDialog.current?.showModal();
				}
			}
		}
		catch (error) {
			console.error('Error uploading file', error);
			setErrorText('File could not be uploaded. Check that the file type is .docx or .pdf')
			errorDialog.current?.showModal();
		}

		e.target.value = "";
	}

	return (
		<div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
			<dialog ref={summaryDialog} className="justify-between px-2 py-2 border rounded">
				<div className="justify-between px-2 py-2 border rounded">
					<div>
						<h1 className="text-lg font-semibold leading-6 mb-4 text-gray-900">File Content Summary:</h1>
						<div className="py-2">
							<ul style={{ listStyleType: "disc" }} className="border rounded px-5">{summary.map((point) =>
								<li>{point}</li>)}
							</ul>
						</div>
					</div>
					<div className="flex items-center justify-center">
						<button onClick={() => summaryDialog.current?.close()} className="justify-between px-2 border rounded">Close</button>
					</div>
				</div>
			</dialog>

			<dialog ref={downloadDialog} className="justify-between px-2 py-2 border rounded">
				<div className="justify-between px-2 py-2 border rounded">
					<div>
						<h1 className="text-lg font-semibold leading-6 mb-4 text-gray-900">Downloading {fileInfo[1]} KB File: {fileInfo[0]}</h1>
						<div className="py-2 flex items-center justify-center">
							<button className="justify-between px-2 border rounded"><a href={download}> Download </a></button>
						</div>
					</div>
					<div className="flex items-center justify-center">
						<button onClick={() => downloadDialog.current?.close()} className="justify-between px-2 border rounded">Close</button>
					</div>
				</div>
			</dialog>

			<dialog ref={errorDialog} className="justify-between px-2 py-2 border rounded">
				<div className="justify-between px-2 py-2 border rounded">
					<div>
						<h1 className="text-lg font-semibold leading-6 mb-4 text-gray-900">Error:</h1>
						<div className="py-2">
							<p className="justify-between px-4 py-4 border rounded"> {errorText} </p>
						</div>
					</div>
					<div className="flex items-center justify-center">
						<button onClick={() => errorDialog.current?.close()} className="justify-between px-2 border rounded">Close</button>
					</div>
				</div>
			</dialog>



			<h1 className="text-lg font-semibold leading-6 mb-4 text-gray-900">My Files</h1>

			<div className="flex mt-2 pt-2 border-t px-2">
				<div className="flex-1">
					<div className="ag-theme-alpine" style={{ height: '70vh' }}>
						<AgGridReact
							ref={gridRef}
							suppressExcelExport={true}
							pagination={true}
							paginationPageSize={20}
							defaultColDef={defaultColDef}
							rowData={rowData}
							columnDefs={colDefs}
							rowSelection={'multiple'}
							editType={'fullRow'}
							pivotPanelShow={'always'}
							reactiveCustomComponents
							enableCellEditingOnBackspace={true}
						/>
						<div>
							<input id="file" type="file" title="Upload a new file" onChange={handleUpload} />
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default UserFiles;
